import { createContext, useContext, useEffect, useState } from "react";
import { useUserAuth } from "./UserAuthContext";
import { apiClient } from "../services/apiClientService";
import debounce from "lodash.debounce";
import {
  getProductMaxCartQuantity as getMaxQty,
  cartLineKey,
} from "../utils/productVariants";
import { trackAddToCart } from "../analytics/events";

const CartContext = createContext();

/** @deprecated import from `../utils/productVariants` — kept for backward compatibility */
export function getProductMaxCartQuantity(product, variantOptionId) {
  return getMaxQty(product, variantOptionId);
}

export const CartProvider = ({ children }) => {
  const { isAuthenticated } = useUserAuth();
  const [cartDrawerOpen, setCartDrawerOpen] = useState(false);
  const [cart, setCart] = useState(() => {
    try {
      const saved = localStorage.getItem("cart");
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      console.warn("Failed to load cart from localStorage:", e.message);
      return [];
    }
  });

  useEffect(() => {
    const normalizeCart = (raw) => {
      if (!Array.isArray(raw)) return [];
      return raw
        .map((item) => ({
          product: item?.product,
          quantity: Number(item?.quantity || 0),
          variantOptionId: item?.variantOptionId || "",
          variantLabel: item?.variantLabel || "",
        }))
        .filter(
          (i) =>
            i?.product?._id &&
            Number.isFinite(i.quantity) &&
            i.quantity > 0,
        );
    };

    const mergeCarts = (a, b) => {
      const map = new Map();
      const add = (item) => {
        const productId = item?.product?._id;
        if (!productId) return;
        const variantOptionId = item?.variantOptionId || "";
        const key = cartLineKey(productId, variantOptionId);
        const prev = map.get(key);
        const quantity = Number(item?.quantity || 0);
        if (!Number.isFinite(quantity) || quantity <= 0) return;

        const product = item.product;
        const base = prev ? prev.quantity : 0;
        const max = getMaxQty(product, variantOptionId);
        const nextQty = Math.max(
          0,
          Math.min(base + quantity, Number.isFinite(max) ? max : base + quantity),
        );
        const merged = {
          product,
          quantity: nextQty,
          variantOptionId,
          variantLabel: item?.variantLabel || prev?.variantLabel || "",
        };
        if (merged.quantity > 0) map.set(key, merged);
      };

      normalizeCart(a).forEach(add);
      normalizeCart(b).forEach(add);
      return Array.from(map.values());
    };

    const syncCart = async () => {
      try {
        const res = await apiClient("GET", "/api/users/cart", null, {
          skipLoading: true,
        });
        if (!res || !Array.isArray(res.cart)) return;

        const backendCart = normalizeCart(
          res.cart.map((item) => ({
            product: item.product,
            quantity: item.quantity,
            variantOptionId: item.variantOptionId || "",
            variantLabel: item.variantLabel || "",
          })),
        );

        // Important UX rule: logging in should NEVER wipe a non-empty local cart.
        const localCart = (() => {
          try {
            return normalizeCart(JSON.parse(localStorage.getItem("cart") || "[]"));
          } catch {
            return normalizeCart(cart);
          }
        })();

        const hasLocal = localCart.length > 0;
        const hasBackend = backendCart.length > 0;

        let next;
        if (hasLocal && !hasBackend) next = localCart;
        else if (!hasLocal && hasBackend) next = backendCart;
        else next = mergeCarts(localCart, backendCart);

        setCart(next);
        localStorage.setItem("cart", JSON.stringify(next));

        // Keep backend in sync immediately after login (especially when backend cart is empty).
        const payload = {
          cart: next.map((item) => ({
            productId: item.product._id,
            quantity: item.quantity,
            variantOptionId: item.variantOptionId || "",
          })),
        };
        await apiClient("POST", "/api/users/cart", payload, { skipLoading: true });
      } catch (err) {
        console.error("Failed to fetch cart from backend:", err.message);
      }
    };

    if (isAuthenticated) {
      syncCart();
    }
  }, [isAuthenticated]); // intentionally not depending on `cart` to avoid re-sync loops

  useEffect(() => {
    const debounced = debounce(async (updatedCart) => {
      try {
        if (!updatedCart || updatedCart.length === 0) {
          return;
        }

        localStorage.setItem("cart", JSON.stringify(updatedCart));

        if (isAuthenticated) {
          const payload = {
            cart: updatedCart.map((item) => ({
              productId: item.product._id,
              quantity: item.quantity,
              variantOptionId: item.variantOptionId || "",
            })),
          };
          await apiClient("POST", "/api/users/cart", payload, {
            skipLoading: true,
          });
        }
      } catch (error) {
        console.error("Cart save failed:", error.message);
      }
    }, 800);

    debounced(cart);
    return () => debounced.cancel();
  }, [cart, isAuthenticated]);

  const addToCart = (product, opts = {}) => {
    const variantOptionId = opts.variantOptionId || "";
    const variantLabel = opts.variantLabel || "";
    const max = getMaxQty(product, variantOptionId);
    if (max <= 0) return;

    setCart((prevCart) => {
      const key = cartLineKey(product._id, variantOptionId);
      const existing = prevCart.find(
        (item) => cartLineKey(item.product._id, item.variantOptionId) === key
      );
      if (existing) {
        const next = Math.min(existing.quantity + 1, max);
        if (next === existing.quantity) return prevCart;
        trackAddToCart(product, { variantOptionId, quantity: next });
        return prevCart.map((item) =>
          cartLineKey(item.product._id, item.variantOptionId) === key
            ? { ...item, quantity: next }
            : item
        );
      }
      trackAddToCart(product, { variantOptionId, quantity: 1 });
      return [
        ...prevCart,
        {
          product,
          quantity: Math.min(1, max),
          variantOptionId,
          variantLabel,
        },
      ];
    });
  };

  const updateQuantity = (productId, delta, variantOptionId = "") => {
    const key = cartLineKey(productId, variantOptionId);
    setCart((prevCart) =>
      prevCart
        .map((item) => {
          if (cartLineKey(item.product._id, item.variantOptionId) !== key)
            return item;
          const max = getMaxQty(item.product, item.variantOptionId);
          const next = item.quantity + delta;
          const clamped = Math.max(0, Math.min(next, max));
          return { ...item, quantity: clamped };
        })
        .filter((item) => item.quantity > 0)
    );
  };

  const removeFromCart = (productId, variantOptionId = "") => {
    const key = cartLineKey(productId, variantOptionId);
    setCart((prevCart) =>
      prevCart.filter(
        (item) => cartLineKey(item.product._id, item.variantOptionId) !== key
      )
    );
  };

  const clearCart = async () => {
    setCart([]);
    localStorage.removeItem("cart");
    if (isAuthenticated) {
      try {
        await apiClient("POST", "/api/users/cart", { cart: [] }, { skipLoading: true });
      } catch (err) {
        console.error("Failed to clear cart on backend:", err.message);
      }
    }
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        addToCart,
        updateQuantity,
        removeFromCart,
        clearCart,
        cartDrawerOpen,
        setCartDrawerOpen,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
